/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ScreenType } from 'client-code/previews';
import { AiringOffer } from 'client-code/airing';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { Channel } from '../entities/Channel';
import { ServicesClientImpl } from '../impls';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Log, QoEAppLogger } from '../logging';
import { MetadataState } from './enums';
import { TAction } from '../types';
import { SocuDataModelImpl } from '../socu/impls';
import { IPlayable } from '../interfaces';
import { PlaybackContentType } from '../socu/enums/PlaybackContentType';
import { VideoPlayerUtils } from './VideoPlayerUtils';
import { IMetadata, IMetadataManager } from './interfaces';

export class MetadataManager implements IMetadataManager {
    private static TAG: string = 'MetadataManager';

    private mAiringCallId: number;

    private mCallbackCounter: number;

    private mMetadata: IMetadata;

    private mMetadataChangeCallbacks: Map<number, Function> = new Map<number, Function>();

    private mMetadataState: MetadataState;

    private mPreviewCallId: number;

    constructor() {
        this.mAiringCallId = 0;
        this.mCallbackCounter = 0;
        this.mMetadata = { airingOffers: [], offerPreview: {} };
        this.mMetadataState = MetadataState.PREPARING;
        this.mPreviewCallId = 0;
    }

    private fetchAiringData(channel: Channel, playerCurrentTime?: number): void {
        this.mAiringCallId = UniqueIdentifier.getNextNetworkCallId();
        new ServicesClientImpl()
            .fetchAiringOffers(channel.stationId!, this.mAiringCallId, playerCurrentTime)
            .then((response) => {
                if (response.networkCallId === this.mAiringCallId) {
                    QoEAppLogger.logWatchContentStart(
                        PlaybackContentType.LIVE,
                        channel,
                        response.airingOffers,
                    );
                    this.mMetadata.airingOffers = response.airingOffers;
                    VideoPlayerUtils.getInstance().setAiringData(response.airingOffers);
                    this.setMetadataState(MetadataState.AIRING_READY);
                }
            })
            .catch((error) => {
                Diagnostics.error(MetadataManager.TAG, 'Failed to fetchAiringData');
                Diagnostics.error(MetadataManager.TAG, 'stacktrace:', error);
                this.mMetadata.airingOffers = [];
                this.mMetadata.offerPreview = {};
                VideoPlayerUtils.getInstance().setAiringData([]);
                this.setMetadataState(MetadataState.AIRING_ERROR);
            });
    }

    private fetchPreviewData(airingOffer: AiringOffer): void {
        const offerId = airingOffer?.offerId;
        this.mPreviewCallId = UniqueIdentifier.getNextNetworkCallId();
        new ServicesClientImpl()
            .fetchOfferPreview(offerId, ScreenType.StandardBanner, this.mPreviewCallId)
            .then((previewResponse: any) => {
                if (previewResponse.networkCallId === this.mPreviewCallId) {
                    this.mMetadata.offerPreview = previewResponse.offerPreview;
                    VideoPlayerUtils.getInstance().setPreviewData(previewResponse.offerPreview);
                    this.setMetadataState(MetadataState.PREVIEW_READY);
                }
            })
            .catch((error) => {
                Diagnostics.error(MetadataManager.TAG, 'Failed to fetchPreviewData');
                Diagnostics.error(MetadataManager.TAG, 'stacktrace:', error);
                this.mMetadata.offerPreview = {};
                VideoPlayerUtils.getInstance().setPreviewData({});
                this.setMetadataState(MetadataState.PREVIEW_ERROR);
            });
    }

    private fetchVodPreviewData(
        channel: Channel,
        screenType: ScreenType = ScreenType.StandardBanner,
    ): void {
        const { kernel: socuActionKernel }: TAction =
            SocuDataModelImpl.getInstance().getSocuAction();
        const offerPreview = this.mMetadata.offerPreview ? this.mMetadata.offerPreview : {};
        this.mMetadata.airingOffers = [];
        VideoPlayerUtils.getInstance().setAiringData([]);
        if (socuActionKernel?.socuOfferId)
            new ServicesClientImpl()
                .getCompleteVodOfferPreviewData(
                    socuActionKernel.socuOfferId,
                    offerPreview,
                    screenType,
                )
                .then((response) => {
                    QoEAppLogger.logWatchContentStart(
                        PlaybackContentType.STARTOVER_BEGINNING,
                        channel,
                        [],
                        response,
                    );
                    Log.d(MetadataManager.TAG, 'Sucsess fetchVodPreviewData', response);
                    this.mMetadata.offerPreview = response;
                    VideoPlayerUtils.getInstance().setPreviewData(response);
                    this.setMetadataState(MetadataState.VOD_PREVIEW_READY);
                })
                .catch((error) => {
                    Diagnostics.error(MetadataManager.TAG, 'Failed to fetchVodPreviewData');
                    Diagnostics.error(MetadataManager.TAG, 'stacktrace:', error);
                    this.setMetadataState(MetadataState.VOD_PREVIEW_ERROR);
                });
    }

    private onMetadataStateChangeHandler(): void {
        switch (this.mMetadataState) {
            case MetadataState.AIRING_READY:
                this.fetchPreviewData(this.mMetadata.airingOffers![0]);
                break;

            default:
        }
        this.mMetadataChangeCallbacks.forEach((callback) => {
            try {
                callback(this.mMetadataState);
            } catch (err) {
                Log.e(MetadataManager.TAG, 'onMetadataStateChangeHandler callback error', err);
            }
        });
    }

    private setMetadataState(state: MetadataState): void {
        Log.i(MetadataManager.TAG, 'Metadata state: ', state);
        this.mMetadataState = state;
        this.onMetadataStateChangeHandler();
    }

    public addMetadataStateChangeListener(callback: Function): number {
        this.mCallbackCounter += 1;
        this.mMetadataChangeCallbacks.set(this.mCallbackCounter, callback);
        return this.mCallbackCounter;
    }

    public getMetadata(): IMetadata {
        return this.mMetadata;
    }

    public getMetadataState(): MetadataState {
        return this.mMetadataState;
    }

    public refreshMetadata(
        playable: IPlayable,
        playerCurrentTime: number,
        screenType?: ScreenType,
    ): void {
        if (playable.playbackContentType === PlaybackContentType.LIVE) {
            this.fetchAiringData(playable.channel, playerCurrentTime);
        } else {
            this.fetchVodPreviewData(playable.channel, screenType);
        }
    }

    public prepareMetadataForPlayable(playable: IPlayable): void {
        if (playable.playbackContentType === PlaybackContentType.LIVE)
            this.fetchAiringData(playable.channel);
        else this.fetchVodPreviewData(playable.channel);
    }

    public removeMetadataStateChangeListener(ref: number): void {
        this.mMetadataChangeCallbacks.delete(ref);
    }
}
